import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Blocks, Layers, Slack, SquarePen, Zap, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
import { REDESIGN_ROW_FILL_INSET_CLASS } from "./compactSessionRowStyles";

/** Primary navigation pinned above the session lists. */

const NAV_ROW = "group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white";
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
const REDESIGN_NAV_ROW = cn("group flex h-8 w-full items-center gap-1.5 rounded-[4px] px-2 type-body-s-regular cursor-pointer transition-none text-neutral-700 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-border)] [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", REDESIGN_ROW_FILL_INSET_CLASS);
function navClasses(variant: CodesmithSidebarVariant) {
  return {
    nav: variant === "redesign" ? "flex flex-col px-2 pb-4" : "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)] pb-2",
    row: variant === "redesign" ? REDESIGN_NAV_ROW : NAV_ROW,
    icon: variant === "redesign" ? "size-4 shrink-0" : "size-[18px] shrink-0"
  };
}

// The shortcut keycaps stay hidden until the row is hovered or
// keyboard-focused, keeping the resting sidebar clean. Opacity (not display)
// preserves the row's layout so the label doesn't shift when they appear.
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(11);
  const {
    icon: Icon,
    label,
    shortcut,
    iconClassName: t1
  } = t0;
  const iconClassName = t1 === undefined ? "size-[18px] shrink-0" : t1;
  let t2;
  if ($[0] !== Icon || $[1] !== iconClassName) {
    t2 = <Icon className={iconClassName} />;
    $[0] = Icon;
    $[1] = iconClassName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <span className="flex-1 truncate text-left">{label}</span>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== shortcut) {
    t4 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[5] = shortcut;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /** Set once Slack is fully connected; the Slack row then opens the DM. */
  slackDmUrl?: string | null;
  /** Holds a placeholder in the Slack row until its connection status resolves. */
  slackLoading?: boolean;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "connect-slack" | "automations" | "mcp-connections" | "machine-definitions" | null;
  className?: string;
  variant?: CodesmithSidebarVariant;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(54);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    mcpConnectionsHref,
    slackDmUrl,
    slackLoading: t1,
    onNavigate,
    newSessionShortcut: t2,
    automationsShortcut: t3,
    activeRow: t4,
    className,
    variant: t5
  } = t0;
  const slackLoading = t1 === undefined ? false : t1;
  const newSessionShortcut = t2 === undefined ? "\u2325\u21E7N" : t2;
  const automationsShortcut = t3 === undefined ? "\u2325\u21E7A" : t3;
  const activeRow = t4 === undefined ? null : t4;
  const variant = t5 === undefined ? "legacy" : t5;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  const machineDefinitionsHref = typeof org === "string" ? `/${org}/codesmith/machine-definitions` : "/codesmith/machine-definitions";
  const connectSlackHref = typeof org === "string" ? `/${org}/codesmith/connect-slack` : "/codesmith/connect-slack";
  let NAV;
  let classes;
  let t6;
  let t7;
  let t8;
  if ($[0] !== activeRow || $[1] !== className || $[2] !== connectSlackHref || $[3] !== newSessionHref || $[4] !== newSessionShortcut || $[5] !== onNavigate || $[6] !== onNewSession || $[7] !== slackDmUrl || $[8] !== slackLoading || $[9] !== variant) {
    classes = navClasses(variant);
    NAV = classes.row;
    const redesign = variant === "redesign";
    t6 = cn(classes.nav, className);
    let t9;
    if ($[15] !== onNewSession) {
      t9 = e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }
        e.preventDefault();
        onNewSession();
      };
      $[15] = onNewSession;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t7 = <Link href={newSessionHref} onClick={t9} aria-keyshortcuts="Alt+Shift+N" aria-current={activeRow === "new-session" ? "page" : undefined} className={cn(NAV, activeRow === "new-session" && NAV_ROW_ACTIVE)}><RowContents icon={SquarePen} label={redesign ? "New Chat" : "New session"} shortcut={newSessionShortcut} iconClassName={classes.icon} /></Link>;
    t8 = slackLoading ? <div className={NAV} aria-label="Loading Slack connection"><Slack className={classes.icon} /><Skeleton className="h-3 w-24 rounded" /></div> : slackDmUrl ? <a href={slackDmUrl} target="_blank" rel="noopener noreferrer" className={NAV}><RowContents icon={Slack} label="Open in Slack" iconClassName={classes.icon} /></a> : <Link href={connectSlackHref} onClick={onNavigate} aria-current={activeRow === "connect-slack" ? "page" : undefined} className={cn(NAV, activeRow === "connect-slack" && NAV_ROW_ACTIVE)}><RowContents icon={Slack} label="Connect Slack" iconClassName={classes.icon} /></Link>;
    $[0] = activeRow;
    $[1] = className;
    $[2] = connectSlackHref;
    $[3] = newSessionHref;
    $[4] = newSessionShortcut;
    $[5] = onNavigate;
    $[6] = onNewSession;
    $[7] = slackDmUrl;
    $[8] = slackLoading;
    $[9] = variant;
    $[10] = NAV;
    $[11] = classes;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    NAV = $[10];
    classes = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[17] !== NAV || $[18] !== activeRow || $[19] !== automationsHref || $[20] !== automationsShortcut || $[21] !== classes.icon || $[22] !== onNavigate || $[23] !== onPrefetchAutomations) {
    t9 = automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={Zap} label="Automations" shortcut={automationsShortcut} iconClassName={classes.icon} /></Link> : null;
    $[17] = NAV;
    $[18] = activeRow;
    $[19] = automationsHref;
    $[20] = automationsShortcut;
    $[21] = classes.icon;
    $[22] = onNavigate;
    $[23] = onPrefetchAutomations;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = activeRow === "mcp-connections" ? "page" : undefined;
  const t11 = activeRow === "mcp-connections" && NAV_ROW_ACTIVE;
  let t12;
  if ($[25] !== NAV || $[26] !== t11) {
    t12 = cn(NAV, t11);
    $[25] = NAV;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== classes.icon) {
    t13 = <RowContents icon={Blocks} label="MCP Connections" iconClassName={classes.icon} />;
    $[28] = classes.icon;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== mcpConnectionsHref || $[31] !== onNavigate || $[32] !== t10 || $[33] !== t12 || $[34] !== t13) {
    t14 = <Link href={mcpConnectionsHref} onClick={onNavigate} aria-current={t10} className={t12}>{t13}</Link>;
    $[30] = mcpConnectionsHref;
    $[31] = onNavigate;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = activeRow === "machine-definitions" ? "page" : undefined;
  const t16 = activeRow === "machine-definitions" && NAV_ROW_ACTIVE;
  let t17;
  if ($[36] !== NAV || $[37] !== t16) {
    t17 = cn(NAV, t16);
    $[36] = NAV;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== classes.icon) {
    t18 = <RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} />;
    $[39] = classes.icon;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== machineDefinitionsHref || $[42] !== onNavigate || $[43] !== t15 || $[44] !== t17 || $[45] !== t18) {
    t19 = <Link href={machineDefinitionsHref} onClick={onNavigate} aria-current={t15} className={t17}>{t18}</Link>;
    $[41] = machineDefinitionsHref;
    $[42] = onNavigate;
    $[43] = t15;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t14 || $[48] !== t19 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t20 = <nav className={t6}>{t7}{t8}{t9}{t14}{t19}</nav>;
    $[47] = t14;
    $[48] = t19;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  return t20;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(41);
  const {
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "legacy" : t1;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  let t2;
  if ($[0] !== isHydrated || $[1] !== org) {
    t2 = isHydrated && readCachedAutomationsEnabled(typeof org === "string" ? org : null);
    $[0] = isHydrated;
    $[1] = org;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const showAutomations = t2;
  let classes;
  let redesign;
  let t3;
  if ($[3] !== className || $[4] !== variant) {
    classes = navClasses(variant);
    redesign = variant === "redesign";
    t3 = cn(classes.nav, className);
    $[3] = className;
    $[4] = variant;
    $[5] = classes;
    $[6] = redesign;
    $[7] = t3;
  } else {
    classes = $[5];
    redesign = $[6];
    t3 = $[7];
  }
  const t4 = redesign ? "New Chat" : "New session";
  let t5;
  if ($[8] !== classes.icon || $[9] !== t4) {
    t5 = <RowContents icon={SquarePen} label={t4} iconClassName={classes.icon} />;
    $[8] = classes.icon;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== classes.row || $[12] !== t5) {
    t6 = <div className={classes.row}>{t5}</div>;
    $[11] = classes.row;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== classes.icon) {
    t7 = <Slack className={classes.icon} />;
    $[14] = classes.icon;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Skeleton className="h-3 w-24 rounded" />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== classes.row || $[18] !== t7) {
    t9 = <div className={classes.row} aria-label="Loading Slack connection">{t7}{t8}</div>;
    $[17] = classes.row;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== classes.icon || $[21] !== classes.row || $[22] !== showAutomations) {
    t10 = showAutomations && <div className={classes.row}><RowContents icon={Zap} label="Automations" iconClassName={classes.icon} /></div>;
    $[20] = classes.icon;
    $[21] = classes.row;
    $[22] = showAutomations;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== classes.icon) {
    t11 = <RowContents icon={Blocks} label="MCP Connections" iconClassName={classes.icon} />;
    $[24] = classes.icon;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== classes.row || $[27] !== t11) {
    t12 = <div className={classes.row}>{t11}</div>;
    $[26] = classes.row;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== classes.icon) {
    t13 = <RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} />;
    $[29] = classes.icon;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== classes.row || $[32] !== t13) {
    t14 = <div className={classes.row}>{t13}</div>;
    $[31] = classes.row;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t10 || $[35] !== t12 || $[36] !== t14 || $[37] !== t3 || $[38] !== t6 || $[39] !== t9) {
    t15 = <nav className={t3}>{t6}{t9}{t10}{t12}{t14}</nav>;
    $[34] = t10;
    $[35] = t12;
    $[36] = t14;
    $[37] = t3;
    $[38] = t6;
    $[39] = t9;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
}